/**
 * Environment suffix per hostname. The suffix selects the per-environment
 * variant of a REACT_APP_* key, e.g. REACT_APP_LAUNCH_DARKLY_CLIENT_ID_STAGE.
 * Each environment is served on its legacy hostname, its public platform
 * hostname and its internal (in-VPC) platform hostname.
 */
const HOSTNAME_SUFFIXES: ReadonlyArray<[RegExp, string]> = [
    // Legacy hostnames
    [/^dev\.mercellforms\.mercell\.com$/, '_DEV'],
    [/^stage\.mercellforms\.mercell\.com$/, '_STAGE'],
    [/^demo\.mercellforms\.mercell\.com$/, '_DEMO'],
    [/^live\.mercellforms\.mercell\.com$/, '_LIVE'],
    // Platform hostnames, public and internal
    [/^meforms\.(internal\.)?forms\.dev\.app\.mercell\.com$/, '_DEV'],
    [/^meforms\.(internal\.)?forms\.test\.app\.mercell\.com$/, '_STAGE'],
    [/^meforms\.(internal\.)?forms\.sales-rc\.app\.mercell\.com$/, '_DEMO'],
    [/^meforms\.(internal\.)?forms\.app\.mercell\.com$/, '_LIVE'],
];

/**
 * Maps a hostname to its environment suffix.
 *
 * An unknown hostname resolves to '' (the unsuffixed keys, which hold the
 * dev values) and never to '_LIVE'. Falling back to production would make a
 * new or misconfigured non-prod hostname silently use production settings
 * such as the production LaunchDarkly client id. A wrong default must fail
 * towards dev, and loudly.
 */
export const resolveEnvSuffix = (hostname: string): string => {
    const host = hostname.toLowerCase();
    const match = HOSTNAME_SUFFIXES.find(([pattern]) => pattern.test(host));
    if (match) {
        return match[1];
    }
    // eslint-disable-next-line no-console
    console.error(
        `[meforms] Unknown origin "${hostname}", using default environment configuration`
    );
    return '';
};

// Resolved once at module load. Non-production builds always use the
// unsuffixed keys from .env / .env.local.
const suffix =
    process.env.NODE_ENV === 'production'
        ? resolveEnvSuffix(window.location.hostname)
        : '';

const envWithSuffix = (key: string) => {
    const keyName = `${key + suffix}`;
    return process.env[keyName];
};

export default envWithSuffix;
